import type { ReactNode } from "react";
import Box from "@mui/material/Box";

/**
 * Matches the orb's own cross-fade (STATE_TAU / `WakeOrb.css`), so the orb and
 * the copy around it move as one when the phase changes.
 */
export const STAGE_TRANSITION_MS = 280;

type Props = {
  open: boolean;
  /**
   * Space between this block and the one above it when open.
   *
   * Owned here rather than by the parent's `gap`, because a `gap` survives at
   * full size around a zero-height child — the block would collapse but leave
   * its spacing behind, and that gap would then vanish in one frame.
   */
  spaceAbove?: number;
  /** Hold the transition back, so an outgoing block can leave first. */
  delayMs?: number;
  /** Fill the parent's cross axis, for children sized with `width: 100%`. */
  stretch?: boolean;
  children: ReactNode;
};

/**
 * Collapses a flex-column child to nothing, and back.
 *
 * Height animates through `grid-template-rows: 1fr -> 0fr`: `1fr` resolves to
 * the content height while the grid has no definite height of its own, so
 * there is nothing to measure and no max-height to guess at. A browser that
 * won't interpolate `fr` snaps instead, which is the behaviour this replaced.
 */
export function CollapseFade({
  open,
  spaceAbove = 0,
  delayMs = 0,
  stretch = false,
  children,
}: Props) {
  const ease = `${STAGE_TRANSITION_MS}ms ease ${delayMs}ms`;

  return (
    <Box
      aria-hidden={open ? undefined : true}
      sx={{
        display: "grid",
        gridTemplateRows: open ? "1fr" : "0fr",
        // `stretch` hands the child the full width so `width: 100%` still
        // resolves against the parent, as it did when it was the flex item.
        justifyItems: stretch ? "stretch" : "center",
        alignSelf: stretch ? "stretch" : undefined,
        pt: open ? `${spaceAbove}px` : 0,
        opacity: open ? 1 : 0,
        transition: `grid-template-rows ${ease}, padding-top ${ease}, opacity ${ease}`,
        "@media (prefers-reduced-motion: reduce)": {
          transition: "none",
        },
      }}
    >
      <Box sx={{ minHeight: 0, overflow: "hidden" }}>{children}</Box>
    </Box>
  );
}
